import { Box, Button, Collapse, Typography } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { addCommas } from "@persian-tools/persian-tools";
import { FormikProps, useFormik } from "formik";
import React, { createContext, useEffect, useState } from "react";
import { DayValue } from "react-modern-calendar-datepicker";
import { useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux";
import ArrowDownSVG from "../../../../media/images/icons/arrow-down";
import BoxCoveredIcon from "../../../../media/images/icons/box-covered";
import BoxOutLineSVG from "../../../../media/images/icons/box-outline";
import LocationSVG from "../../../../media/images/icons/location";
import TruckOutlineSVG from "../../../../media/images/icons/truck-outline";
import {
  setDialogDescription,
  setDialogStatus,
  setDialogTitle,
  setOpenDialog,
} from "../../../../redux/features/dialogSlice";
import { setOwnerInfo } from "../../../../redux/features/ownerSlice";
import { fetcherPost } from "../../../../services/fetcher";
import CustomAutoComplete from "../../../components/CustomAutoComplete";
import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomInputLabel from "../../../components/CustomInputLabel";
import MobileHeader from "../../../layout/OwnerLayout/Header";
import CustomTimepicker from "../../../components/TimePicker";
import GetWithUnitInput from "./GetWithUnitInput";

export interface IStepItem {
  index: number;
  title: string;
  status: "pending" | "inprogress" | "done" | "error";
  Icon: (color: string) => JSX.Element;
}

export interface ILoaderInfoFormik {
  description: string;
  descriptionArr: any[];
  truckCount: number;
  loaderType: null | any;
}

export interface IOtherPersonFormik {
  senderNameObject: any | null;
  senderNIDObject: any | null;
  nationID: string;
  senderName: string;
  SenderAddressCord: null | any;
  senderTitle: string;
  senderTitleValue: string;
  senderType: string;
  senderCity: any | null;
  senderPostalCode: string;
  senderAddressStr: string;
}

export interface IReciverFormik {
  reciverSelected: null | any;
  nationIdSelected: null | any;
  reciverNameSelected: null | any;
  ReceiverAddressCord: null | any;
  reciverTitleValue: string;
  receiverTitle: string;
  receiverInfo: any | null;
  receiverCity: any | null;
  receiverPostalCode?: string;
  receiverAddressStr?: string;
}

export interface IWhoIsFormik {
  value: "1" | "2" | "3";
}

export interface IGoodInfoFormik {
  goodsInfo: any | null;
  goodsInfoStr: any | null;
  packageTypeInfo: any | null;
  packageTypeInfoStr: any | null;
  description: string;
  truckWeight: string;
  truckWeightTo: string;
  goodsCost: string;
  goodsCode: string;
  payType: number;
  orderCarryType: number;
}

export interface ILoadingDateFormik {
  loadingTime: string;
  loadingToTime: string;
}

export interface IInsertOrderFormik {
  company: any | null;
  senderNationID: "";
  senderAddressStr: "";
  senderName: "";
  senderTitle: "";
  senderCity: any | null;
  senderInfo: {
    nationID: "";
    name: "";
    title: "";
  };
}

interface IFormikContext {
  whoIsFormik: FormikProps<IWhoIsFormik>;
  otherPersonFormik: FormikProps<IOtherPersonFormik>;
  reciverFormik: FormikProps<IReciverFormik>;
  goodsInfoFormik: FormikProps<IGoodInfoFormik>;
  loaderInfoFormik: FormikProps<ILoaderInfoFormik>;
  loadingDateFotmik: FormikProps<ILoadingDateFormik>;
  insertOrderFormik: FormikProps<IInsertOrderFormik>;
  isFirstRender: React.MutableRefObject<boolean>;
  setStepItems: React.Dispatch<React.SetStateAction<IStepItem[]>>;
  stepItems: IStepItem[];
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  activeIndex: number;
  setBeginDate: React.Dispatch<React.SetStateAction<DayValue>>;
  beginDate: DayValue;
  setEndDate: React.Dispatch<React.SetStateAction<DayValue>>;
  endDate: DayValue;
  loadingTimeStart: { hour: string; min: string } | null;
  setLoadingTimeStart: React.Dispatch<
    React.SetStateAction<{ hour: string; min: string } | null>
  >;
  loadingTimeEnd: { hour: string; min: string } | null;
  setLoadingTimeEnd: React.Dispatch<
    React.SetStateAction<{ hour: string; min: string } | null>
  >;
  companyModal: boolean;
  setCompanyModal: React.Dispatch<React.SetStateAction<boolean>>;
}

// Kept for the existing AddOrder child components so no unrelated files need to change.
export const FormikContext = createContext<IFormikContext>(
  {} as IFormikContext
);

interface ISimpleOrderForm {
  origin: any | null;
  originAddress: string;
  destination: any | null;
  goodsInfo: any | null;
  packageTypeInfo: any | null;
  loaderGroup: any | null;
  truckCount: string;
  weightFrom: string;
  truckWeightTo: string;
  fare: string;
  description: string;
  loaderType: any | null;
  fleetCount: string;
  loadingTime: string;
  loadingToTime: string;
}

const simpleOrderValidator = yup.object().shape({
  origin: yup.object().nullable().required("این فیلد الزامی است!"),
  originAddress: yup.string().trim().required("این فیلد الزامی است!"),
  destination: yup.object().nullable().required("این فیلد الزامی است!"),
  goodsInfo: yup.object().nullable().required("این فیلد الزامی است!"),
  packageTypeInfo: yup.object().nullable().required("این فیلد الزامی است!"),
  loaderGroup: yup.object().nullable().required("این فیلد الزامی است!"),
  truckCount: yup
    .string()
    .required("این فیلد الزامی است!")
    .test("min-trucks", "تعداد کامیون باید حداقل ۱ باشد", (value) =>
      Boolean(value && Number(value) >= 1)
    ),
  weightFrom: yup
    .string()
    .required("این فیلد الزامی است!")
    .test("positive-weight-from", "وزن محموله باید بیشتر از صفر باشد", (value) =>
      Boolean(value && Number(value) > 0)
    ),
  truckWeightTo: yup
    .string()
    .required("این فیلد الزامی است!")
    .test("positive-weight-to", "وزن محموله باید بیشتر از صفر باشد", (value) =>
      Boolean(value && Number(value) > 0)
    )
    .test("weight-range", "وزن تا نباید کمتر از وزن از باشد", function (value) {
      const from = Number(this.parent.weightFrom);
      const to = Number(value);
      if (!from || !to) return true;
      return to >= from;
    }),
  fare: yup
    .string()
    .required("این فیلد الزامی است!")
    .test("positive-fare", "کرایه باید بیشتر از صفر باشد", (value) =>
      Boolean(value && Number(value) > 0)
    ),
});

const asOptions = (data: any) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    ...item,
    label: item?.label ?? item?.name ?? "",
    value: item?.value ?? item?.id ?? item?.code,
  }));

export default function AddOrder() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ownerInfo } = useAppSelector((state) => state.owner);

  const [showMore, setShowMore] = useState(false);
  const [originInputValue, setOriginInputValue] = useState("");
  const [destinationInputValue, setDestinationInputValue] = useState("");
  const [goodsInputValue, setGoodsInputValue] = useState("");
  const [packageInputValue, setPackageInputValue] = useState("");
  const [loaderGroupInputValue, setLoaderGroupInputValue] = useState("");
  const [loaderInputValue, setLoaderInputValue] = useState("");
  const [originOptions, setOriginOptions] = useState<any[]>([]);
  const [destinationOptions, setDestinationOptions] = useState<any[]>([]);
  const [goodsOptions, setGoodsOptions] = useState<any[]>([]);
  const [packageOptions, setPackageOptions] = useState<any[]>([]);
  const [loaderGroupOptions, setLoaderGroupOptions] = useState<any[]>([]);
  const [loaderOptions, setLoaderOptions] = useState<any[]>([]);
  const [weightFromDisplay, setWeightFromDisplay] = useState("");
  const [weightToDisplay, setWeightToDisplay] = useState("");
  const [fareDisplay, setFareDisplay] = useState("");
  const [loadingTimeStart, setLoadingTimeStart] = useState<{
    hour: string;
    min: string;
  } | null>(null);
  const [loadingTimeEnd, setLoadingTimeEnd] = useState<{
    hour: string;
    min: string;
  } | null>(null);

  const { trigger: getOwnerInfo } = useSWRMutation(
    [`/Driver/GetOwnerUserInfo`, localStorage.getItem("access_token")],
    fetcherPost,
    {
      onSuccess: (data) => dispatch(setOwnerInfo(data)),
    }
  );

  const { trigger: takeOriginCities } = useSWRMutation(
    [`/City/TakeCity`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setOriginOptions(data || []) }
  );

  const { trigger: takeDestinationCities } = useSWRMutation(
    [`/City/TakeCity`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setDestinationOptions(data || []) }
  );

  const { trigger: takeGoods } = useSWRMutation(
    [`/Goods/Take`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setGoodsOptions(data || []) }
  );

  const { trigger: takePackageTypes } = useSWRMutation(
    [`/PackageType/Take`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setPackageOptions(asOptions(data)) }
  );

  const { trigger: takeLoaderGroups } = useSWRMutation(
    [`/LoaderType/TakeLoaderGroup`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setLoaderGroupOptions(asOptions(data)) }
  );

  const { trigger: takeLoaderTypes } = useSWRMutation(
    [`/LoaderType/TakeLoaderGroup`, localStorage.getItem("access_token")],
    fetcherPost,
    { onSuccess: (data) => setLoaderOptions(asOptions(data)) }
  );

  useEffect(() => {
    if (!ownerInfo) getOwnerInfo({});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      takeOriginCities({ name: originInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [originInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      takeDestinationCities({ name: destinationInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [destinationInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      takeGoods({ name: goodsInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [goodsInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      takePackageTypes({ name: packageInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [packageInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      takeLoaderGroups({ name: loaderGroupInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [loaderGroupInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      takeLoaderTypes({ name: loaderInputValue || null });
    }, 500);
    return () => clearTimeout(timer);
  }, [loaderInputValue]);

  const { trigger: insertOrder, isMutating } = useSWRMutation(
    [`/PreOrder/Insert`, localStorage.getItem("access_token")],
    fetcherPost,
    {
      onSuccess: () => {
        dispatch(setOpenDialog(true));
        dispatch(setDialogStatus("success"));
        dispatch(setDialogTitle("سفارش شما با موفقیت ثبت شد!"));
        setTimeout(() => dispatch(setOpenDialog(false)), 3000);
        navigate("../manage-orders");
      },
      onError: (error: any) => {
        dispatch(setOpenDialog(true));
        dispatch(setDialogStatus("error"));
        dispatch(setDialogTitle("ثبت سفارش انجام نشد"));
        dispatch(
          setDialogDescription(
            error?.response?.data?.message ||
              error?.response?.data?.title ||
              "لطفاً اطلاعات واردشده را بررسی کرده و دوباره تلاش کنید."
          )
        );
      },
    }
  );

  const formik = useFormik<ISimpleOrderForm>({
    initialValues: {
      origin: null,
      originAddress: "",
      destination: null,
      goodsInfo: null,
      packageTypeInfo: null,
      loaderGroup: null,
      truckCount: "",
      weightFrom: "",
      truckWeightTo: "",
      fare: "",
      description: "",
      loaderType: null,
      fleetCount: "",
      loadingTime: "",
      loadingToTime: "",
    },
    validationSchema: simpleOrderValidator,
    validateOnChange: true,
    onSubmit: async (values) => {
      let currentOwnerInfo = ownerInfo;
      if (!currentOwnerInfo) {
        try {
          currentOwnerInfo = await getOwnerInfo({});
        } catch {
          // The insert endpoint can still use the authenticated user context.
        }
      }

      const ownerName = [currentOwnerInfo?.foreName, currentOwnerInfo?.surName]
        .filter(Boolean)
        .join(" ");
      const weightFrom = Number(values.weightFrom);
      const weightTo = Number(values.truckWeightTo);
      const fare = Number(values.fare);
      const truckCount = Number(values.truckCount);
      const fleetCount = values.fleetCount ? Number(values.fleetCount) : 0;
      const originAddress = values.originAddress.trim();

      const payload: any = {
        senderInfo: {
          nationID: currentOwnerInfo?.userName || "",
          name: ownerName,
          title: "",
        },
        senderNationID: currentOwnerInfo?.userName || "",
        senderName: ownerName,
        senderTitle: "",
        senderCity: values.origin,
        senderAddressStr: originAddress,
        senderAddress: {
          cityCode: values.origin?.value,
          cityName: values.origin?.label,
          address: originAddress,
          postalCode: "",
        },
        receiverInfo: {
          nationID: "",
          name: "",
          title: "",
        },
        receiverNationID: "",
        reciverName: "",
        receiverCity: values.destination,
        receiverAddressStr: values.destination?.label || "",
        receiverAddress: {
          cityCode: values.destination?.value,
          cityName: values.destination?.label,
          address: values.destination?.label || "",
          postalCode: "",
        },
        goodsInfo: values.goodsInfo?.value,
        packageTypeInfo:
          values.packageTypeInfo?.value?.id != null
            ? values.packageTypeInfo.value
            : {
                id:
                  values.packageTypeInfo?.id?.id ??
                  values.packageTypeInfo?.id ??
                  values.packageTypeInfo?.value,
                name:
                  values.packageTypeInfo?.name ??
                  values.packageTypeInfo?.label,
              },
        truckWeight: weightFrom,
        truckWeightTo: weightTo,
        TruckWeightTo: weightTo,
        fare,
        description: values.description.trim(),
        payType: 1,
        orderCarryType: 1,
        truckCount: truckCount || fleetCount,
      };

      const loaderGroupName =
        values.loaderGroup?.label || values.loaderType?.label;
      if (loaderGroupName) payload.LoaderTypeGroup = loaderGroupName;

      if (values.loadingTime) payload.loadingTime = values.loadingTime;
      if (values.loadingToTime) payload.loadingToTime = values.loadingToTime;

      insertOrder(payload);
    },
  });

  const handleNumberChange = (
    value: string,
    field: "weightFrom" | "truckWeightTo" | "fare",
    setDisplay: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const numericValue = value.replace(/[^\d]/g, "");
    formik.setFieldValue(field, numericValue);
    setDisplay(numericValue ? addCommas(numericValue) : "");
  };

  return (
    <>
      <MobileHeader
        title={
          <Typography
            sx={{ fontSize: "16px", fontWeight: "700" }}
            color="secondary.main"
          >
            ثبت سفارش
          </Typography>
        }
      />

      <Box sx={{ px: "8px", pb: "24px" }}>
        <Box
          sx={{
            mt: 1,
            border: "1px solid",
            borderColor: blueGrey[100],
            backgroundColor: "bgColor.white",
            borderRadius: "16px",
            p: "16px 12px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <Box>
            <Typography
              color="secondary.main"
              sx={{ fontSize: "16px", fontWeight: 700 }}
            >
              اطلاعات سفارش
            </Typography>
            <Typography
              color="textColor.light"
              sx={{ mt: "2px", fontSize: "12px", fontWeight: 500 }}
            >
              اطلاعات اصلی بار را وارد کنید؛ سایر مشخصات اختیاری هستند.
            </Typography>
          </Box>

          <Box>
            <CustomInputLabel required>{"مبدا"}</CustomInputLabel>
            <CustomAutoComplete
              inputValue={originInputValue}
              setInputValue={setOriginInputValue}
              type="origin"
              placeHolder="شهر مبدا را انتخاب کنید*"
              setValue={(selected) => formik.setFieldValue("origin", selected)}
              value={formik.values.origin}
              error={formik.touched.origin && Boolean(formik.errors.origin)}
              errorMessage={formik.touched.origin && formik.errors.origin}
              options={originOptions}
              innerIcon={<LocationSVG color={blueGrey[400]} />}
              mb="0px"
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"آدرس محل بارگیری"}</CustomInputLabel>
            <CustomInput
              textArea
              type="text"
              name="originAddress"
              value={formik.values.originAddress}
              handelChange={formik.handleChange}
              handelOnBlur={formik.handleBlur}
              placHolder="آدرس دقیق محل بارگیری را بنویسید*"
              error={
                formik.touched.originAddress &&
                Boolean(formik.errors.originAddress)
              }
              errorMessage={
                formik.touched.originAddress && formik.errors.originAddress
              }
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"مقصد"}</CustomInputLabel>
            <CustomAutoComplete
              inputValue={destinationInputValue}
              setInputValue={setDestinationInputValue}
              type="destination"
              placeHolder="شهر مقصد را انتخاب کنید*"
              setValue={(selected) =>
                formik.setFieldValue("destination", selected)
              }
              value={formik.values.destination}
              error={
                formik.touched.destination && Boolean(formik.errors.destination)
              }
              errorMessage={
                formik.touched.destination && formik.errors.destination
              }
              options={destinationOptions}
              innerIcon={<LocationSVG color={blueGrey[400]} />}
              mb="0px"
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"محموله"}</CustomInputLabel>
            <CustomAutoComplete
              inputValue={goodsInputValue}
              setInputValue={setGoodsInputValue}
              type="goodsInfo"
              placeHolder="نوع محموله را انتخاب کنید*"
              setValue={(selected) =>
                formik.setFieldValue("goodsInfo", selected)
              }
              value={formik.values.goodsInfo}
              error={
                formik.touched.goodsInfo && Boolean(formik.errors.goodsInfo)
              }
              errorMessage={formik.touched.goodsInfo && formik.errors.goodsInfo}
              options={goodsOptions}
              innerIcon={<BoxOutLineSVG color={blueGrey[400]} />}
              mb="0px"
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"نوع بسته بندی"}</CustomInputLabel>
            <CustomAutoComplete
              inputValue={packageInputValue}
              setInputValue={setPackageInputValue}
              type="packageTypeInfo"
              placeHolder="نوع بسته بندی را انتخاب کنید*"
              setValue={(selected) =>
                formik.setFieldValue("packageTypeInfo", selected)
              }
              value={formik.values.packageTypeInfo}
              error={
                formik.touched.packageTypeInfo &&
                Boolean(formik.errors.packageTypeInfo)
              }
              errorMessage={
                formik.touched.packageTypeInfo && formik.errors.packageTypeInfo
              }
              options={packageOptions}
              innerIcon={<BoxCoveredIcon color={blueGrey[400]} />}
              mb="0px"
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"گروه بارگیری"}</CustomInputLabel>
            <CustomAutoComplete
              inputValue={loaderGroupInputValue}
              setInputValue={setLoaderGroupInputValue}
              type="loaderGroup"
              placeHolder="گروه بارگیری را انتخاب کنید*"
              setValue={(selected) =>
                formik.setFieldValue("loaderGroup", selected)
              }
              value={formik.values.loaderGroup}
              error={
                formik.touched.loaderGroup && Boolean(formik.errors.loaderGroup)
              }
              errorMessage={
                formik.touched.loaderGroup && formik.errors.loaderGroup
              }
              options={loaderGroupOptions}
              innerIcon={<TruckOutlineSVG color={blueGrey[400]} />}
              mb="0px"
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"تعداد کامیون"}</CustomInputLabel>
            <CustomInput
              type="numeric"
              name="truckCount"
              value={formik.values.truckCount}
              handelChange={(e) => {
                const value = e.target.value.replace(/[^\d]/g, "");
                formik.setFieldValue("truckCount", value);
              }}
              handelOnBlur={formik.handleBlur}
              placHolder="تعداد کامیون را وارد کنید*"
              error={
                formik.touched.truckCount && Boolean(formik.errors.truckCount)
              }
              errorMessage={
                formik.touched.truckCount && formik.errors.truckCount
              }
              startAdornment={<TruckOutlineSVG color={blueGrey[400]} />}
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"وزن محموله از"}</CustomInputLabel>
            <GetWithUnitInput
              name="weightFrom"
              value={weightFromDisplay}
              handleChange={(e) =>
                handleNumberChange(
                  e.target.value,
                  "weightFrom",
                  setWeightFromDisplay
                )
              }
              unit="کیلوگرم"
              placeholder="وزن محموله از را وارد کنید*"
              error={
                formik.touched.weightFrom && Boolean(formik.errors.weightFrom)
              }
              errorMessage={
                formik.touched.weightFrom && formik.errors.weightFrom
              }
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"وزن محموله تا"}</CustomInputLabel>
            <GetWithUnitInput
              name="truckWeightTo"
              value={weightToDisplay}
              handleChange={(e) =>
                handleNumberChange(
                  e.target.value,
                  "truckWeightTo",
                  setWeightToDisplay
                )
              }
              unit="کیلوگرم"
              placeholder="وزن محموله تا را وارد کنید*"
              error={
                formik.touched.truckWeightTo &&
                Boolean(formik.errors.truckWeightTo)
              }
              errorMessage={
                formik.touched.truckWeightTo && formik.errors.truckWeightTo
              }
            />
          </Box>

          <Box>
            <CustomInputLabel required>{"کرایه"}</CustomInputLabel>
            <GetWithUnitInput
              name="fare"
              value={fareDisplay}
              handleChange={(e) =>
                handleNumberChange(e.target.value, "fare", setFareDisplay)
              }
              unit="ریال"
              placeholder="مبلغ کرایه را وارد کنید*"
              error={formik.touched.fare && Boolean(formik.errors.fare)}
              errorMessage={formik.touched.fare && formik.errors.fare}
            />
          </Box>

          <Box>
            <CustomInputLabel>{"توضیحات"}</CustomInputLabel>
            <CustomInput
              textArea
              type="text"
              name="description"
              value={formik.values.description}
              handelChange={formik.handleChange}
              handelOnBlur={formik.handleBlur}
              placHolder="در صورت نیاز توضیحات سفارش را بنویسید"
            />
          </Box>

          <Button
            onClick={() => setShowMore((prev) => !prev)}
            disableRipple
            sx={{
              width: "100%",
              minHeight: "48px",
              px: "12px",
              border: "1px solid",
              borderColor: blueGrey[200],
              borderRadius: "10px",
              backgroundColor: blueGrey[50],
              color: "secondary.main",
              display: "flex",
              justifyContent: "space-between",
              "&:hover": { backgroundColor: blueGrey[50] },
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
              اطلاعات اضافه
            </Typography>
            <Box
              sx={{
                display: "flex",
                transform: showMore ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <ArrowDownSVG color="#0D4884" size={22} />
            </Box>
          </Button>

          <Collapse in={showMore} unmountOnExit>
            <Box
              sx={{
                pt: "4px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              <Box>
                <CustomInputLabel>{"نوع بارگیر (اختیاری)"}</CustomInputLabel>
                <CustomAutoComplete
                  inputValue={loaderInputValue}
                  setInputValue={setLoaderInputValue}
                  type="loaderType"
                  placeHolder="نوع بارگیر پیشنهادی را انتخاب کنید"
                  setValue={(selected) =>
                    formik.setFieldValue("loaderType", selected)
                  }
                  value={formik.values.loaderType}
                  options={loaderOptions}
                  innerIcon={<TruckOutlineSVG color={blueGrey[400]} />}
                  mb="0px"
                />
              </Box>

              <Box>
                <CustomInputLabel>{"تعداد ناوگان (اختیاری)"}</CustomInputLabel>
                <CustomInput
                  type="numeric"
                  name="fleetCount"
                  value={formik.values.fleetCount}
                  handelChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, "");
                    formik.setFieldValue("fleetCount", value);
                  }}
                  placHolder="تعداد ناوگان مورد نیاز را وارد کنید"
                  startAdornment={<TruckOutlineSVG color={blueGrey[400]} />}
                />
              </Box>

              <Box>
                <CustomInputLabel>{"ساعت بارگیری از"}</CustomInputLabel>
                <CustomTimepicker
                  selectedValue={loadingTimeStart}
                  setSelectedValue={setLoadingTimeStart}
                  label="ساعت بارگیری از"
                  onChange={(value) =>
                    formik.setFieldValue("loadingTime", value)
                  }
                  handleResetDate={() => {
                    formik.setFieldValue("loadingTime", "");
                    setLoadingTimeStart(null);
                  }}
                  value={{}}
                  error={false}
                  errorMessage=""
                />
              </Box>

              <Box>
                <CustomInputLabel>{"ساعت بارگیری تا"}</CustomInputLabel>
                <CustomTimepicker
                  selectedValue={loadingTimeEnd}
                  setSelectedValue={setLoadingTimeEnd}
                  label="ساعت بارگیری تا"
                  onChange={(value) =>
                    formik.setFieldValue("loadingToTime", value)
                  }
                  handleResetDate={() => {
                    formik.setFieldValue("loadingToTime", "");
                    setLoadingTimeEnd(null);
                  }}
                  value={{}}
                  error={false}
                  errorMessage=""
                />
              </Box>
            </Box>
          </Collapse>

          <CustomButton
            onClick={() => formik.handleSubmit()}
            loading={isMutating}
            width="100%"
            flexDirection="row"
            title="ثبت سفارش"
            justifyContent="center"
            bgColor="success.main"
            px="10px"
            py="13px"
            mt="4px"
            borderRadius="10px"
          />
        </Box>
      </Box>
    </>
  );
}
